import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Play,
  X,
  ChevronLeft,
  ChevronRight,
  FileSignature,
  FileText,
  Brain,
  MessageSquare,
  Shield,
  UserPlus,
  Users,
  Building,
  MessageCircle,
} from "lucide-react";
import { openExternalLink } from "../../services/site/linkService";
import { InfoService } from "../../services/site/InfoService";
import homeVideo from "../../assets/videos/home.mp4";
import { SLIDER_IMAGE_ONE, SLIDER_IMAGE_THREE, SLIDER_IMAGE_TWO , SLIDER_IMAGE_FOUR, SLIDER_IMAGE_FIVE, SLIDER_IMAGE_SIX, SLIDER_IMAGE_SEVEN} from "../../services/ImageService";

const HeroSlider: React.FC = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [showVideo, setShowVideo] = useState(false);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);

  // Define slides with use cases for each product
  const slides = [
    // Slide 0: HDT Booking — Flagship WhatsApp Ordering SaaS
    {
      type: "usecase",
      icon: MessageCircle,
      title: "Run Your Restaurant from WhatsApp.",
      subtitle: "Literally — orders, kitchen, inventory, vendors, AI reports.",
      description:
        "Customers order on WhatsApp. Your kitchen sees it instantly. Stock auto-deducts. Vendor invoices auto-parse from Gmail. Reports write themselves. All in one place.",
      features: [
        "Conversational WhatsApp ordering — no app install",
        "Kitchen Display + recipe-linked stock",
        "Gmail invoice parse → WhatsApp approval",
        "AI Report Chat in plain English",
      ],
      image: SLIDER_IMAGE_ONE,
      productPath: "/whatsapp-ordering",
      flagship: true,
    },
    // Slide 1: Original Hero
    {
      type: "hero",
      title: (
        <>
          Empowering Businesses with{" "}
          <span className="gradient-text">Cutting-Edge Digital Solutions</span>
        </>
      ),
      subtitle:
        "From seamless electronic signatures to intelligent data extraction, we transform how you work, making your operations more efficient, secure, and future-ready.",
      showTechIcons: true,
      showButtons: true,
    },
    // Slide 2: Digital Signature Use Case
    {
      type: "usecase",
      icon: FileSignature,
      title: "Still Struggling with Manual Signing?",
      subtitle: "Try Modern Secure Digital Signing",
      description:
        "Say goodbye to printing, scanning, and mailing. Sign contracts, agreements, and legal documents electronically from anywhere, anytime.",
      features: [
        "Upload documents in any format",
        "Drag & drop signature fields",
        "Multi-party signing support",
        "Legally binding & compliant",
      ],
      image: SLIDER_IMAGE_ONE,
      productPath: "/digital-signature-solution",
    },
    // Slide 3: Document Automation Use Case
    {
      type: "usecase",
      icon: FileText,
      title: "Tired of Repetitive Document Work?",
      subtitle: "Automate Your Document Workflows",
      description:
        "Transform hours of manual document processing into minutes. Automatically fill templates, merge files, and generate documents at scale.",
      features: [
        "CRM data auto-population",
        "Template customization",
        "Batch processing capabilities",
        "Multiple output formats",
      ],
      image: SLIDER_IMAGE_TWO,
      productPath: "/pdf-word-template-filling",
    },
    // Slide 4: AI Data Extraction Use Case
    {
      type: "usecase",
      icon: Brain,
      title: "Drowning in Unstructured Data?",
      subtitle: "Let AI Extract It For You",
      description:
        "Stop manually copying data from invoices, resumes, and contracts. Our AI reads, extracts, and organizes information automatically.",
      features: [
        "Resume parsing & analysis",
        "Invoice data extraction",
        "Contract information mining",
        "Multi-language support",
      ],
      image: SLIDER_IMAGE_THREE,
      productPath: "/ai-data-extraction",
    },
    // Slide 5: Chatbot Use Case
    {
      type: "usecase",
      icon: MessageSquare,
      title: "Customer Support Overwhelming?",
      subtitle: "Deploy Intelligent Chatbots 24/7",
      description:
        "Provide instant, accurate responses to customer queries around the clock. Reduce support costs while improving customer satisfaction.",
      features: [
        "24/7 customer support",
        "Industry-specific knowledge",
        "Multi-channel deployment",
        "Continuous learning",
      ],
      image: SLIDER_IMAGE_FOUR,
      productPath: "/intelligent-chatbot",
    },
    // Slide 6: Employee Onboarding Use Case
    {
      type: "usecase",
      icon: UserPlus,
      title: "Employee Onboarding Taking Forever?",
      subtitle: "Complete It in Minutes, Not Days",
      description:
        "Streamline the entire onboarding process. Collect documents, extract information, fill forms, and get signatures - all in one automated flow.",
      features: [
        "Document collection & upload",
        "AI-powered data extraction",
        "Auto-fill employment forms",
        "Digital signature integration",
      ],
      image: SLIDER_IMAGE_FIVE,
      productPath: "/employee-onboarding",
    },
    // Slide 7: Client Onboarding Use Case
    {
      type: "usecase",
      icon: Users,
      title: "Losing Clients During Onboarding?",
      subtitle: "Make It Fast & Frictionless",
      description:
        "Speed up client acquisition with smart onboarding. Reduce drop-off rates and get clients up and running in record time.",
      features: [
        "Smart information forms",
        "Document verification",
        "Template-based agreements",
        "Secure e-signature workflows",
      ],
      image: SLIDER_IMAGE_SIX,
      productPath: "/client-onboarding",
    },
    // Slide 8: Vendor Onboarding Use Case
    {
      type: "usecase",
      icon: Building,
      title: "Vendor Onboarding Too Complex?",
      subtitle: "Simplify & Secure the Process",
      description:
        "Automate vendor registration, verification, and compliance. Ensure all requirements are met while reducing processing time.",
      features: [
        "Automated verification",
        "Compliance validation",
        "Digital contract signing",
        "Secure document management",
      ],
      image: SLIDER_IMAGE_SEVEN,
      productPath: "/vendor-onboarding",
    },
  ];

  const totalSlides = slides.length;

  // Auto-play functionality
  useEffect(() => {
    if (!isAutoPlaying) return;

    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % totalSlides);
    }, 5000); // Change slide every 5 seconds

    return () => clearInterval(interval);
  }, [isAutoPlaying, totalSlides]);

  const nextSlide = () => {
    setIsAutoPlaying(false);
    setCurrentSlide((prev) => (prev + 1) % totalSlides);
  };

  const prevSlide = () => {
    setIsAutoPlaying(false);
    setCurrentSlide((prev) => (prev - 1 + totalSlides) % totalSlides);
  };

  const goToSlide = (index: number) => {
    setIsAutoPlaying(false);
    setCurrentSlide(index);
  };

  const currentSlideData = slides[currentSlide];

  return (
    <section className="hero-slider" id="home">
      <div className="hero-decorative">
        <div className="circle circle1"></div>
        <div className="circle circle2"></div>
      </div>

      <div className="container">
        {/* Flagship badge on Booking slide */}
        {currentSlide === 0 && (
          <motion.div
            className="experience-badge flagship-badge"
            animate={{ scale: [1, 1.05, 1] }}
            transition={{ duration: 2, repeat: Infinity }}
          >
            🆕 NEW · Flagship Product
          </motion.div>
        )}
        {/* Experience badge on original hero */}
        {currentSlide === 1 && (
          <motion.div
            className="experience-badge"
            animate={{ scale: [1, 1.05, 1] }}
            transition={{ duration: 2, repeat: Infinity }}
          >
            6+ Years Experience
          </motion.div>
        )}

        {/* Slider Content */}
        <AnimatePresence mode="wait">
          <motion.div
            key={currentSlide}
            className="hero-content"
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -50 }}
            transition={{ duration: 0.5 }}
          >
            {currentSlideData.type === "hero" ? (
              // Original Hero Slide
              <>
                <h1 className="hero-title">{currentSlideData.title}</h1>
                <p className="hero-subtitle">{currentSlideData.subtitle}</p>

                {/* Tech Icons */}
{currentSlideData.showTechIcons && (
  <div className="section m-0 p-0">
    <div className="container m-0">
      <div className="columns is-centered">
        <div className="column is-narrow">
          <motion.div
            className="tech-icons-container"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
          >
            <div className="tech-icon">🤖</div>
            <div className="tech-icon">📱</div>
            <div className="tech-icon">💻</div>
            <div className="tech-icon">🔐</div>
            <div className="tech-icon">☁️</div>
          </motion.div>
        </div>
      </div>
    </div>
  </div>
)}



                {currentSlideData.showButtons && (
                  <div className="hero-buttons">
                    <button
                      onClick={() => openExternalLink(InfoService?.loginLink)}
                      className="btn btn-primary"
                    >
                      Get Started
                    </button>
                    <button
                      className="btn btn-video"
                      onClick={() => setShowVideo(true)}
                    >
                      <Play size={20} /> Watch Demo
                    </button>
                  </div>
                )}
              </>
            ) : (
              // Use Case Slide
              <div className="usecase-slide">
                <div className="usecase-content">
                  <motion.div
                    className="usecase-icon-wrapper"
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ delay: 0.2, type: "spring" }}
                  >
                    {currentSlideData.icon && (
                      <currentSlideData.icon size={64} className="usecase-icon" />
                    )}
                  </motion.div>

                  <motion.h2
                    className="usecase-title"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.3 }}
                  >
                    {currentSlideData.title}
                  </motion.h2>

                  <motion.h3
                    className="usecase-subtitle"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.4 }}
                  >
                    {currentSlideData.subtitle}
                  </motion.h3>

                  <motion.p
                    className="usecase-description"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.5 }}
                  >
                    {currentSlideData.description}
                  </motion.p>

                  <motion.div
                    className="usecase-features"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.6 }}
                  >
                    {currentSlideData.features?.map((feature, idx) => (
                      <motion.div
                        key={idx}
                        className="usecase-feature-item"
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: 0.7 + idx * 0.1 }}
                      >
                        <span className="feature-check">✓</span>
                        <span>{feature}</span>
                      </motion.div>
                    ))}
                  </motion.div>

                  <motion.div
                    className="usecase-buttons"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.8 }}
                  >
                    <button
                      onClick={() =>
                        openExternalLink(
                          currentSlideData.flagship
                            ? InfoService?.bookingLink
                            : InfoService?.loginLink +
                                currentSlideData.productPath
                        )
                      }
                      className="btn btn-primary"
                    >
                      Try Free
                    </button>
                    <button
                      onClick={() =>
                        (window.location.href = currentSlideData.productPath!)
                      }
                      className="btn btn-secondary"
                    >
                      Learn More
                    </button>
                  </motion.div>
                </div>

                {/* Visual representation - could be an image or illustration */}
             <motion.div
  className="usecase-visual"
  initial={{ opacity: 0, scale: 0.8 }}
  animate={{ opacity: 1, scale: 1 }}
  transition={{ delay: 0.4 }}
>
  <div className="visual-placeholder">
    <img
      src={currentSlideData.image}
      alt="Usecase Visual"
      className="visual-image"
    />
    <div className="visual-glow"></div>
  </div>
</motion.div>

              </div>
            )}
          </motion.div>
        </AnimatePresence>

        {/* Navigation Controls */}
        <div className="slider-controls">
          <button
            className="slider-nav-btn slider-prev"
            onClick={prevSlide}
            aria-label="Previous slide"
          >
            <ChevronLeft size={24} />
          </button>

          <div className="slider-dots">
            {slides.map((_, idx) => (
              <button
                key={idx}
                className={`slider-dot ${idx === currentSlide ? "active" : ""}`}
                onClick={() => goToSlide(idx)}
                aria-label={`Go to slide ${idx + 1}`}
              />
            ))}
          </div>

          <button
            className="slider-nav-btn slider-next"
            onClick={nextSlide}
            aria-label="Next slide"
          >
            <ChevronRight size={24} />
          </button>
        </div>

        {/* Auto-play Toggle */}
        <button
          className="slider-autoplay-toggle"
          onClick={() => setIsAutoPlaying(!isAutoPlaying)}
          aria-label={isAutoPlaying ? "Pause autoplay" : "Resume autoplay"}
        >
          {isAutoPlaying ? "❚❚" : "▶"}
        </button>

        {/* Video Modal */}
        <AnimatePresence>
          {showVideo && (
            <motion.div
              className="video-modal"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setShowVideo(false)}
            >
              <motion.div
                className="video-container"
                initial={{ scale: 0.8 }}
                animate={{ scale: 1 }}
                exit={{ scale: 0.8 }}
                onClick={(e) => e.stopPropagation()}
              >
                <button
                  className="close-video"
                  onClick={() => setShowVideo(false)}
                >
                  <X size={24} />
                </button>
                <div className="video-wrapper">
                  <video
                    width="100%"
                    height="100%"
                    controls
                    autoPlay
                    src={homeVideo}
                  >
                    Your browser does not support the video tag.
                  </video>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </section>
  );
};

export default HeroSlider;
